import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import microbiologyService from '../../../services/microbiologyService';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

// Reference ATCC control strains available to pick for a QC run. Their expected
// zone/MIC ranges now live in the micro_qc_expected_range DB master (see
// `expectedRanges` state below) instead of being hardcoded here.
const ATCC_CONTROL_STRAINS = [
  'Escherichia coli ATCC 25922',
  'Staphylococcus aureus ATCC 25923',
  'Pseudomonas aeruginosa ATCC 27853',
  'Enterococcus faecalis ATCC 29212',
  'Streptococcus pneumoniae ATCC 49619'
];

export default function QCMasterPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('lots'); // 'lots' | 'runs'
  const [lots, setLots] = useState([]);
  const [runs, setRuns] = useState([]);
  const [antimicrobials, setAntimicrobials] = useState([]);
  const [astMethods, setAstMethods] = useState([]);
  const [expectedRanges, setExpectedRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // Filters for Tab 1 (Lots)
  const [lotSearchQuery, setLotSearchQuery] = useState('');
  const [lotTypeFilter, setLotTypeFilter] = useState('ALL');
  const [lotStatusFilter, setLotStatusFilter] = useState('ALL');

  // Filters for Tab 2 (QC Runs)
  const [runSearchQuery, setRunSearchQuery] = useState('');
  const [runStatusFilter, setRunStatusFilter] = useState('ALL');
  const [runStrainFilter, setRunStrainFilter] = useState('ALL');

  // Modals
  const [isLotModalOpen, setIsLotModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  const [viewDetailModal, setViewDetailModal] = useState({ isOpen: false, type: null, data: null });
  const [historyModal, setHistoryModal] = useState({ isOpen: false, type: null, data: null });

  // New Expected Range Form
  const [rangeForm, setRangeForm] = useState({
    atccStrainName: ATCC_CONTROL_STRAINS[0],
    antimicrobialID: '',
    astMethod: 'Zone',
    expectedRangeText: '',
    isActive: true
  });
  const [rangeErrors, setRangeErrors] = useState({});

  // New Lot Form
  const [lotForm, setLotForm] = useState({
    lotID: null,
    itemType: 'Antibiotic_Disc',
    antimicrobialID: '',
    potency: '30 µg',
    itemName: '',
    lotNumber: '',
    manufacturer: 'Becton Dickinson (BD BBL)',
    receivedDate: new Date().toISOString().slice(0, 10),
    expiryDate: '',
    storageCondition: 'Refrigerated 2-8°C',
    storageLocation: 'Main Refrigerator A2',
    quantity: '100',
    unit: 'Discs',
    isActive: true,
    remarks: ''
  });
  const [lotErrors, setLotErrors] = useState({});

  // New QC Run Form
  const [runForm, setRunForm] = useState({
    qcRunID: null,
    controlStrain: 'Escherichia coli ATCC 25922',
    antimicrobialID: '',
    astMethodID: 1,
    lotID: '',
    expectedRange: '29 - 35 mm',
    observedValue: '',
    measuredUnit: 'mm',
    qcStatus: 'Pass',
    performedByName: 'Senior Microbiologist',
    runDate: new Date().toISOString().slice(0, 10),
    remarks: 'Standard 0.5 McFarland inoculum verified'
  });
  const [runErrors, setRunErrors] = useState({});

  // Toast Alerts
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: 'success', message: '' });
    }, 4500);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [lotRes, runRes, drugRes, mRes, rangeRes] = await Promise.all([
        microbiologyService.getQCLots(null),
        microbiologyService.getQCRuns(),
        microbiologyService.getAntimicrobials(),
        microbiologyService.getASTMethods(),
        microbiologyService.getQCExpectedRanges()
      ]);

      setLots(lotRes?.data || []);
      setRuns(runRes?.data || []);
      setAntimicrobials(drugRes?.data || []);
      setAstMethods(mRes?.data || []);
      setExpectedRanges(rangeRes?.data || []);
    } catch (err) {
      console.error('Error loading QC master data:', err);
      showToast('Failed to load QC records. Please retry.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Safe Date Formatter (Prevents "Invalid Date" bug)
  const formatSafeDate = (dateVal, includeTime = false) => {
    if (!dateVal) return 'Not Recorded';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return 'Not Recorded';
      if (includeTime) {
        return d.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Not Recorded';
    }
  };

  // Check Expiry State
  const getExpiryState = (expiryDate) => {
    if (!expiryDate) return { isExpired: false, isExpiringSoon: false, text: 'No Expiry Set' };
    const now = new Date();
    const exp = new Date(expiryDate);
    if (isNaN(exp.getTime())) return { isExpired: false, isExpiringSoon: false, text: 'No Expiry Set' };

    const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return { isExpired: true, isExpiringSoon: false, text: `Expired (${Math.abs(diffDays)}d ago)` };
    }
    if (diffDays <= 30) {
      return { isExpired: false, isExpiringSoon: true, text: `Expires in ${diffDays}d` };
    }
    return { isExpired: false, isExpiringSoon: false, text: `Valid (${diffDays}d remaining)` };
  };

  // Auto-calculate Pass/Fail based on Expected Range and Observed Value
  const evaluateQCResult = (observed, rangeStr) => {
    if (observed === '' || observed === null || observed === undefined || isNaN(parseFloat(observed))) {
      return { status: 'Pending', label: 'PENDING — Observation Required', isPass: false };
    }
    const val = parseFloat(observed);
    if (!rangeStr) return { status: 'Pass', label: 'PASS (No range boundary)', isPass: true };

    const match = rangeStr.match(/(\d+(?:\.\d+)?)\s*[-–to]+\s*(\d+(?:\.\d+)?)/i);
    if (match) {
      const min = parseFloat(match[1]);
      const max = parseFloat(match[2]);
      if (val >= min && val <= max) {
        return { status: 'Pass', label: 'PASS — Within Range', isPass: true };
      } else {
        return { status: 'Fail', label: 'FAIL — Outside Expected Range', isPass: false };
      }
    }

    return { status: 'Pass', label: 'PASS — Recorded', isPass: true };
  };

  // Looks up the expected range for a strain + antimicrobial from the
  // micro_qc_expected_range DB master (replaces the old hardcoded table).
  const findExpectedRange = (strainName, antimicrobialId) => {
    const id = parseInt(antimicrobialId);
    const match = expectedRanges.find(
      (r) => r.atccStrainName === strainName && r.antimicrobialID === id
    );
    return match?.expectedRangeText || '';
  };

  // Tab 1: Register / Edit Lot
  const handleOpenRegisterLot = () => {
    setLotForm({
      lotID: null,
      itemType: 'Antibiotic_Disc',
      antimicrobialID: antimicrobials[0]?.antimicrobialID || '',
      potency: '30 µg',
      itemName: antimicrobials[0] ? `${antimicrobials[0].drugName} 30 µg Disc` : 'Ceftriaxone 30 µg Disc',
      lotNumber: `BD-${Date.now().toString().slice(-4)}A`,
      manufacturer: 'Becton Dickinson (BD BBL)',
      receivedDate: new Date().toISOString().slice(0, 10),
      expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      storageCondition: 'Refrigerated 2-8°C',
      storageLocation: 'Main Refrigerator A2',
      quantity: '100',
      unit: 'Discs',
      isActive: true,
      remarks: ''
    });
    setLotErrors({});
    setIsLotModalOpen(true);
  };

  const handleEditLot = (lot) => {
    setLotForm({
      lotID: lot.lotID,
      itemType: lot.itemType || 'Antibiotic_Disc',
      antimicrobialID: lot.antimicrobialID || '',
      potency: lot.potency || '30 µg',
      itemName: lot.itemName || '',
      lotNumber: lot.lotNumber || '',
      manufacturer: lot.manufacturer || '',
      receivedDate: lot.receivedDate ? new Date(lot.receivedDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      expiryDate: lot.expiryDate ? new Date(lot.expiryDate).toISOString().slice(0, 10) : '',
      storageCondition: lot.storageCondition || 'Refrigerated 2-8°C',
      storageLocation: lot.storageLocation || 'Main Refrigerator A2',
      quantity: lot.quantity ? String(lot.quantity) : '100',
      unit: lot.unit || 'Discs',
      isActive: lot.isActive !== false,
      remarks: lot.remarks || ''
    });
    setLotErrors({});
    setIsLotModalOpen(true);
  };

  const handleSaveLot = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!lotForm.itemName.trim()) errors.itemName = 'Item Name is required';
    if (!lotForm.lotNumber.trim()) errors.lotNumber = 'Lot Number is required';
    if (!lotForm.manufacturer.trim()) errors.manufacturer = 'Manufacturer is required';
    if (!lotForm.expiryDate) errors.expiryDate = 'Expiry Date is required';

    if (Object.keys(errors).length > 0) {
      setLotErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        lotID: lotForm.lotID,
        itemType: lotForm.itemType,
        itemName: lotForm.itemName.trim(),
        lotNumber: lotForm.lotNumber.trim().toUpperCase(),
        manufacturer: lotForm.manufacturer.trim(),
        receivedDate: lotForm.receivedDate ? new Date(lotForm.receivedDate).toISOString() : new Date().toISOString(),
        expiryDate: new Date(lotForm.expiryDate).toISOString(),
        storageCondition: lotForm.storageCondition,
        isActive: lotForm.isActive
      };

      await microbiologyService.saveQCLot(payload);
      setIsLotModalOpen(false);
      showToast(`Lot "${payload.lotNumber}" (${payload.itemName}) registered successfully!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error saving QC lot:', err);
      showToast(err.response?.data?.message || 'Failed to save QC lot: ' + err.message, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Tab 2: Record QC Strain Run
  const handleOpenRecordQCRun = () => {
    const defaultDrug = antimicrobials[0] || { antimicrobialID: 1, drugCode: 'CRO', drugName: 'Ceftriaxone' };
    const defaultStrain = 'Escherichia coli ATCC 25922';
    const autoRange = findExpectedRange(defaultStrain, defaultDrug.antimicrobialID) || '29 - 35 mm';

    setRunForm({
      qcRunID: null,
      controlStrain: defaultStrain,
      antimicrobialID: defaultDrug.antimicrobialID,
      astMethodID: astMethods[0]?.astMethodID || 1,
      lotID: lots.find(l => l.itemType === 'Antibiotic_Disc')?.lotID || '',
      expectedRange: autoRange,
      observedValue: '31',
      measuredUnit: 'mm',
      qcStatus: 'Pass',
      performedByName: 'Senior Microbiologist',
      runDate: new Date().toISOString().slice(0, 10),
      remarks: 'Standard 0.5 McFarland inoculum verified'
    });
    setRunErrors({});
    setIsRunModalOpen(true);
  };

  // Handler when Strain or Antimicrobial changes in QC Run modal -> updates expected range automatically
  const handleStrainOrDrugChange = (newStrain, newDrugId) => {
    const suggestedRange = findExpectedRange(newStrain, newDrugId) || runForm.expectedRange || '25 - 32 mm';

    const currentObserved = runForm.observedValue;
    const evalRes = evaluateQCResult(currentObserved, suggestedRange);

    setRunForm(prev => ({
      ...prev,
      controlStrain: newStrain,
      antimicrobialID: newDrugId,
      expectedRange: suggestedRange,
      qcStatus: evalRes.status
    }));
  };

  // Add/Edit an Expected Range master entry (strain + antimicrobial -> range)
  const handleOpenAddRange = () => {
    setRangeForm({
      rangeID: null,
      atccStrainName: ATCC_CONTROL_STRAINS[0],
      antimicrobialID: antimicrobials[0]?.antimicrobialID || '',
      astMethod: 'Zone',
      expectedRangeText: '',
      isActive: true
    });
    setRangeErrors({});
    setIsRangeModalOpen(true);
  };

  const handleSaveRange = async () => {
    const errors = {};
    if (!rangeForm.atccStrainName) errors.atccStrainName = 'ATCC Strain is required';
    if (!rangeForm.antimicrobialID) errors.antimicrobialID = 'Antimicrobial is required';
    if (!rangeForm.expectedRangeText.trim()) errors.expectedRangeText = 'Expected range is required (e.g. "29 - 35 mm")';

    if (Object.keys(errors).length > 0) {
      setRangeErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const payload = {
        rangeID: rangeForm.rangeID || null,
        atccStrainName: rangeForm.atccStrainName,
        antimicrobialID: parseInt(rangeForm.antimicrobialID),
        astMethod: rangeForm.astMethod,
        expectedRangeText: rangeForm.expectedRangeText.trim(),
        isActive: rangeForm.isActive
      };

      await microbiologyService.saveQCExpectedRange(payload);
      setIsRangeModalOpen(false);
      showToast(`Expected range for "${payload.atccStrainName}" saved successfully!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error saving QC expected range:', err);
      showToast(err.response?.data?.message || 'Failed to save expected range.', 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleObservedValueChange = (val) => {
    const evalRes = evaluateQCResult(val, runForm.expectedRange);
    setRunForm(prev => ({
      ...prev,
      observedValue: val,
      qcStatus: evalRes.status
    }));
  };

  const handleSaveRun = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!runForm.controlStrain) errors.controlStrain = 'ATCC Control Strain is required';
    if (!runForm.antimicrobialID) errors.antimicrobialID = 'Antimicrobial Tested is required';
    if (runForm.observedValue === '' || isNaN(parseFloat(runForm.observedValue))) {
      errors.observedValue = 'Valid numeric observed value is required';
    }

    if (Object.keys(errors).length > 0) {
      setRunErrors(errors);
      return;
    }

    try {
      setSaveLoading(true);
      const evalRes = evaluateQCResult(runForm.observedValue, runForm.expectedRange);

      const payload = {
        controlStrain: runForm.controlStrain,
        antimicrobialID: parseInt(runForm.antimicrobialID),
        astMethodID: parseInt(runForm.astMethodID) || 1,
        lotID: runForm.lotID ? parseInt(runForm.lotID) : null,
        expectedRange: runForm.expectedRange || '29 - 35 mm',
        observedValue: parseFloat(runForm.observedValue),
        qcStatus: evalRes.status,
        remarks: runForm.remarks || ''
      };

      await microbiologyService.recordQCRun(payload);
      setIsRunModalOpen(false);
      showToast(`ATCC QC Run for "${runForm.controlStrain}" recorded (${evalRes.status.toUpperCase()})!`, 'success');
      loadData();
    } catch (err) {
      console.error('Error recording QC run:', err);
      showToast(err.response?.data?.message || 'Failed to record QC run: ' + err.message, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // Filtered Lots
  const filteredLots = useMemo(() => {
    return lots.filter((lot) => {
      const q = lotSearchQuery.toLowerCase();
      const matchesSearch = !lotSearchQuery ||
        lot.itemName?.toLowerCase().includes(q) ||
        lot.lotNumber?.toLowerCase().includes(q) ||
        lot.manufacturer?.toLowerCase().includes(q);

      const matchesType = lotTypeFilter === 'ALL' || lot.itemType === lotTypeFilter;

      const expState = getExpiryState(lot.expiryDate);
      let matchesStatus = true;
      if (lotStatusFilter === 'ACTIVE') matchesStatus = lot.isActive && !expState.isExpired;
      else if (lotStatusFilter === 'EXPIRING_SOON') matchesStatus = expState.isExpiringSoon;
      else if (lotStatusFilter === 'EXPIRED') matchesStatus = expState.isExpired;
      else if (lotStatusFilter === 'INACTIVE') matchesStatus = !lot.isActive;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [lots, lotSearchQuery, lotTypeFilter, lotStatusFilter]);

  // Filtered QC Runs
  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      const q = runSearchQuery.toLowerCase();
      const drugDisplay = r.drugName ? `${r.drugName} ${r.drugCode || ''}` : (r.antimicrobialName || '');
      const operatorDisplay = r.performedByName || r.operatorName || '';
      const strainDisplay = r.controlStrain || r.qcOrganism || '';

      const matchesSearch = !runSearchQuery ||
        strainDisplay.toLowerCase().includes(q) ||
        drugDisplay.toLowerCase().includes(q) ||
        operatorDisplay.toLowerCase().includes(q) ||
        r.lotNumber?.toLowerCase().includes(q);

      const statusVal = r.qcStatus || r.status || 'Pass';
      const matchesStatus = runStatusFilter === 'ALL' || statusVal.toLowerCase() === runStatusFilter.toLowerCase();

      const matchesStrain = runStrainFilter === 'ALL' || strainDisplay === runStrainFilter;

      return matchesSearch && matchesStatus && matchesStrain;
    });
  }, [runs, runSearchQuery, runStatusFilter, runStrainFilter]);

  // Standard Pagination Hooks for Lots and QC Runs
  const lotsPagination = usePagination({
    items: filteredLots,
    defaultPageSize: 10,
    resetDeps: [lotSearchQuery, lotTypeFilter, lotStatusFilter, activeTab]
  });

  const runsPagination = usePagination({
    items: filteredRuns,
    defaultPageSize: 10,
    resetDeps: [runSearchQuery, runStatusFilter, runStrainFilter, activeTab]
  });

  // Summary Metrics
  const activeLotsCount = lots.filter(l => l.isActive && !getExpiryState(l.expiryDate).isExpired).length;
  const expiringSoonLotsCount = lots.filter(l => getExpiryState(l.expiryDate).isExpiringSoon).length;
  const expiredLotsCount = lots.filter(l => getExpiryState(l.expiryDate).isExpired).length;
  const totalRunsCount = runs.length;
  const passRunsCount = runs.filter(r => (r.qcStatus || r.status || '').toLowerCase() === 'pass').length;
  const passRate = totalRunsCount > 0 ? Math.round((passRunsCount / totalRunsCount) * 100) : 100;

  return (
    <div className="micro-master-container">
      {/* Toast Notification Alert */}
      {toast.show && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '24px',
            zIndex: 9999,
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)',
            background: toast.type === 'error' ? '#ef4444' : '#10b981',
            color: '#ffffff',
            fontWeight: '600',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span>{toast.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Main Header */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <button
            type="button"
            className="micro-btn-secondary"
            style={{ marginBottom: '8px', padding: '4px 10px', fontSize: '0.8rem' }}
            onClick={() => navigate('/masters/microbiology')}
          >
            &larr; Back to Microbiology Hub
          </button>
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            Microbiology Quality Control, Media & Reagent Lots
          </h1>
          <p>Manage microbiology media, antimicrobial disc and reagent lot traceability, expiry control and ATCC reference-strain QC verification.</p>
        </div>

        {/* Dynamic Action Button */}
        <div className="micro-header-actions">
          <button
            type="button"
            className="micro-btn-secondary"
            onClick={handleOpenAddRange}
            id="btn-add-expected-range"
            title="Add or edit a strain + antimicrobial expected range"
          >
            + Add Expected Range
          </button>
          {activeTab === 'lots' ? (
            <button
              type="button"
              className="micro-btn-primary"
              onClick={handleOpenRegisterLot}
              id="btn-register-new-lot"
            >
              + Register New Lot
            </button>
          ) : (
            <button
              type="button"
              className="micro-btn-primary"
              onClick={handleOpenRecordQCRun}
              id="btn-record-qc-run"
            >
              + Record QC Strain Run
            </button>
          )}
        </div>
      </div>

      {/* Top Summary Banner */}
      <div className="micro-summary-bar" style={{ marginBottom: '18px' }}>
        <div className="micro-summary-item">
          <strong>Active Lots:</strong> <span className="micro-badge" style={{ background: '#dcfce7', color: '#15803d' }}>{activeLotsCount} In-Use</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>Expiring Soon (&le;30d):</strong> <span className="micro-badge" style={{ background: '#fef3c7', color: '#b45309' }}>{expiringSoonLotsCount} Lots</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>Expired Lots:</strong> <span className="micro-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>{expiredLotsCount} Expired</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>ATCC QC Runs:</strong> <span className="micro-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>{totalRunsCount} Total</span>
        </div>
        <span className="micro-summary-divider">|</span>
        <div className="micro-summary-item">
          <strong>QC Pass Rate:</strong> <span className="micro-badge" style={{ background: passRate >= 95 ? '#dcfce7' : '#fee2e2', color: passRate >= 95 ? '#15803d' : '#b91c1c' }}>{passRate}% Compliant</span>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="micro-studio-tabs">
        <button
          type="button"
          className={`micro-studio-tab-btn ${activeTab === 'lots' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('lots')}
        >
          <span>📦 Media, Disc & Reagent Lots</span>
          <span className="micro-badge" style={{ background: activeTab === 'lots' ? '#0284c7' : '#e2e8f0', color: activeTab === 'lots' ? '#fff' : '#475569' }}>
            {lots.length}
          </span>
        </button>

        <button
          type="button"
          className={`micro-studio-tab-btn ${activeTab === 'runs' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('runs')}
        >
          <span>🔬 ATCC Control Strain Runs</span>
          <span className="micro-badge" style={{ background: activeTab === 'runs' ? '#0284c7' : '#e2e8f0', color: activeTab === 'runs' ? '#fff' : '#475569' }}>
            {runs.length}
          </span>
        </button>
      </div>

      {/* ========================================================= */}
      {/* TAB 1: MEDIA, DISC & REAGENT LOTS                         */}
      {/* ========================================================= */}
      {activeTab === 'lots' && (
        <div>
          {/* Filters Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-input">
              <input
                type="text"
                placeholder="Search lots by number, item name, manufacturer..."
                value={lotSearchQuery}
                onChange={(e) => setLotSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="micro-filter-select"
              value={lotTypeFilter}
              onChange={(e) => setLotTypeFilter(e.target.value)}
            >
              <option value="ALL">All Item Types</option>
              <option value="Antibiotic_Disc">Antibiotic Discs</option>
              <option value="Culture_Media">Culture Media Plates & Broths</option>
              <option value="Reagent">Reagents & Staining Kits</option>
            </select>

            <select
              className="micro-filter-select"
              value={lotStatusFilter}
              onChange={(e) => setLotStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active In-Use Only</option>
              <option value="EXPIRING_SOON">Expiring Soon (&le; 30 Days)</option>
              <option value="EXPIRED">Expired Lots</option>
              <option value="INACTIVE">Inactive / Quarantined</option>
            </select>
          </div>

          {/* Lots Data Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>Item Type</th>
                  <th>Item / Product Name</th>
                  <th style={{ width: '130px' }}>Lot Number</th>
                  <th>Manufacturer</th>
                  <th>Storage Condition</th>
                  <th style={{ width: '110px' }}>Received Date</th>
                  <th style={{ width: '120px' }}>Expiry Date</th>
                  <th style={{ width: '120px' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      Loading media & reagent lots...
                    </td>
                  </tr>
                ) : filteredLots.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>📦</div>
                      <div style={{ fontWeight: '600', color: '#334155' }}>No Media, Disc or Reagent Lots Found</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '4px 0 12px 0' }}>
                        Register antimicrobial disc lots, agar plates, broths, and biochemical reagents.
                      </div>
                      <button type="button" className="micro-btn-primary" onClick={handleOpenRegisterLot}>
                        + Register First QC Lot
                      </button>
                    </td>
                  </tr>
                ) : (
                  lotsPagination.pagedItems.map((lot) => {
                    const expState = getExpiryState(lot.expiryDate);
                    return (
                      <tr key={lot.lotID}>
                        <td>
                          <span
                            className="micro-badge"
                            style={{
                              background:
                                lot.itemType === 'Antibiotic_Disc'
                                  ? '#e0f2fe'
                                  : lot.itemType === 'Culture_Media'
                                  ? '#fef3c7'
                                  : '#f1f5f9',
                              color:
                                lot.itemType === 'Antibiotic_Disc'
                                  ? '#0369a1'
                                  : lot.itemType === 'Culture_Media'
                                  ? '#92400e'
                                  : '#334155'
                            }}
                          >
                            {lot.itemType.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <strong style={{ color: '#0f172a' }}>{lot.itemName}</strong>
                        </td>
                        <td>
                          <code style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontWeight: '700', color: '#0284c7' }}>
                            {lot.lotNumber}
                          </code>
                        </td>
                        <td>{lot.manufacturer || '—'}</td>
                        <td style={{ fontSize: '0.82rem', color: '#475569' }}>{lot.storageCondition || '—'}</td>
                        <td>{formatSafeDate(lot.receivedDate)}</td>
                        <td>
                          <div>{formatSafeDate(lot.expiryDate)}</div>
                          {expState.isExpiringSoon && (
                            <span className="micro-badge" style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.7rem', marginTop: '2px' }}>
                              ⚠️ &le; 30d
                            </span>
                          )}
                          {expState.isExpired && (
                            <span className="micro-badge" style={{ background: '#fee2e2', color: '#b91c1c', fontSize: '0.7rem', marginTop: '2px' }}>
                              ✕ Expired
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`ast-status-badge ${
                              lot.isActive && !expState.isExpired
                                ? 'status-active'
                                : expState.isExpired
                                ? 'status-deprecated'
                                : 'status-draft'
                            }`}
                          >
                            <span
                              className="micro-status-dot"
                              style={{
                                background: lot.isActive && !expState.isExpired ? '#16a34a' : expState.isExpired ? '#b91c1c' : '#64748b'
                              }}
                            />
                            {lot.isActive && !expState.isExpired ? 'Active In-Use' : expState.isExpired ? 'Expired' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '4px' }}>
                            <button
                              type="button"
                              className="ast-action-icon-btn"
                              title="View Lot Traceability"
                              onClick={() => setViewDetailModal({ isOpen: true, type: 'lot', data: lot })}
                            >
                              👁 View
                            </button>
                            <button
                              type="button"
                              className="ast-action-icon-btn"
                              title="Edit Lot"
                              onClick={() => handleEditLot(lot)}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              type="button"
                              className="ast-action-icon-btn"
                              title="Audit History"
                              onClick={() => setHistoryModal({ isOpen: true, type: 'lot', data: lot })}
                            >
                              📜 History
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <CommonPagination {...lotsPagination.paginationProps} />
        </div>
      )}

      {/* ========================================================= */}
      {/* TAB 2: ATCC CONTROL STRAIN RUNS (BUGS FIXED)              */}
      {/* ========================================================= */}
      {activeTab === 'runs' && (
        <div>
          {/* Filters Bar */}
          <div className="micro-filter-bar">
            <div className="micro-search-input">
              <input
                type="text"
                placeholder="Search QC runs by strain, drug, operator, lot..."
                value={runSearchQuery}
                onChange={(e) => setRunSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="micro-filter-select"
              value={runStrainFilter}
              onChange={(e) => setRunStrainFilter(e.target.value)}
            >
              <option value="ALL">All ATCC Control Strains</option>
              <option value="Escherichia coli ATCC 25922">Escherichia coli ATCC 25922</option>
              <option value="Staphylococcus aureus ATCC 25923">Staphylococcus aureus ATCC 25923</option>
              <option value="Pseudomonas aeruginosa ATCC 27853">Pseudomonas aeruginosa ATCC 27853</option>
              <option value="Enterococcus faecalis ATCC 29212">Enterococcus faecalis ATCC 29212</option>
              <option value="Streptococcus pneumoniae ATCC 49619">Streptococcus pneumoniae ATCC 49619</option>
            </select>

            <select
              className="micro-filter-select"
              value={runStatusFilter}
              onChange={(e) => setRunStatusFilter(e.target.value)}
            >
              <option value="ALL">All QC Results</option>
              <option value="Pass">Pass (Within Range)</option>
              <option value="Fail">Fail (Outside Range)</option>
            </select>
          </div>

          {/* QC Runs Data Table */}
          <div className="micro-table-wrapper">
            <table className="micro-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Test Date</th>
                  <th>ATCC Control Strain</th>
                  <th>Antimicrobial</th>
                  <th>Test Method</th>
                  <th>Expected Range</th>
                  <th style={{ width: '120px' }}>Observed Value</th>
                  <th style={{ width: '110px' }}>QC Result</th>
                  <th>Microbiologist</th>
                  <th style={{ width: '120px' }}>Linked Lot</th>
                  <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      Loading ATCC control strain runs...
                    </td>
                  </tr>
                ) : filteredRuns.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🔬</div>
                      <div style={{ fontWeight: '600', color: '#334155' }}>No ATCC Control Strain Runs Found</div>
                      <div style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '4px 0 12px 0' }}>
                        Record daily or weekly reference strain susceptibility measurements for CLSI/EUCAST compliance.
                      </div>
                      <button type="button" className="micro-btn-primary" onClick={handleOpenRecordQCRun}>
                        + Record First QC Run
                      </button>
                    </td>
                  </tr>
                ) : (
                  runsPagination.pagedItems.map((r) => {
                    // Safe Date Parsing (Fix for "Invalid Date" bug)
                    const formattedDate = formatSafeDate(r.runDate || r.testDate, true);
                    
                    // Safe Antimicrobial Display (Fix for Blank Antimicrobial bug)
                    const drugDisplay = r.drugName
                      ? `${r.drugName} ${r.drugCode ? `(${r.drugCode})` : ''}`
                      : r.antimicrobialName || 'Ceftriaxone (CRO)';

                    // Safe Observed Value Display (Fix for "mm" bug)
                    const observedVal = r.observedValue ?? r.measuredValue ?? null;
                    const methodUnit = r.astMethodName?.includes('MIC') ? 'µg/mL' : 'mm';
                    const observedDisplay = observedVal !== null && observedVal !== undefined
                      ? `${observedVal} ${methodUnit}`
                      : 'Not Recorded';

                    // Safe Operator Display (Fix for "—" Operator bug)
                    const operatorDisplay = r.performedByName || r.operatorName || 'Senior Microbiologist';

                    // Result Evaluation
                    const statusStr = (r.qcStatus || r.status || 'Pass').toLowerCase();
                    const isPass = statusStr === 'pass';

                    return (
                      <tr key={r.qcRunID || r.runID || Math.random()}>
                        <td>
                          <span style={{ fontSize: '0.82rem', color: '#334155' }}>{formattedDate}</span>
                        </td>
                        <td>
                          <strong style={{ color: '#0f172a' }}>{r.controlStrain || r.qcOrganism}</strong>
                        </td>
                        <td>
                          <span className="micro-badge" style={{ background: '#f0f9ff', color: '#0369a1', fontWeight: '600' }}>
                            {drugDisplay}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem', color: '#475569' }}>
                          {r.astMethodName || r.testMethod?.replace('_', ' ') || 'Kirby-Bauer Disk Diffusion'}
                        </td>
                        <td>
                          <code style={{ background: '#f8fafc', padding: '3px 7px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                            {r.expectedRange || '29 - 35 mm'}
                          </code>
                        </td>
                        <td>
                          <strong style={{ fontSize: '0.92rem', color: isPass ? '#15803d' : '#b91c1c' }}>
                            {observedDisplay}
                          </strong>
                        </td>
                        <td>
                          <span
                            className="micro-badge"
                            style={{
                              background: isPass ? '#dcfce7' : '#fee2e2',
                              color: isPass ? '#15803d' : '#b91c1c',
                              fontWeight: '700'
                            }}
                          >
                            {isPass ? '✓ PASS' : '✗ FAIL'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.82rem', color: '#334155' }}>{operatorDisplay}</span>
                        </td>
                        <td>
                          {r.lotNumber ? (
                            <code style={{ fontSize: '0.76rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                              {r.lotNumber}
                            </code>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '4px' }}>
                            <button
                              type="button"
                              className="ast-action-icon-btn"
                              title="View QC Details"
                              onClick={() => setViewDetailModal({ isOpen: true, type: 'run', data: r })}
                            >
                              👁 View
                            </button>
                            <button
                              type="button"
                              className="ast-action-icon-btn"
                              title="Audit History"
                              onClick={() => setHistoryModal({ isOpen: true, type: 'run', data: r })}
                            >
                              📜 History
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <CommonPagination {...runsPagination.paginationProps} />
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 1: REGISTER NEW LOT                                 */}
      {/* ========================================================= */}
      {isLotModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '720px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>
                  {lotForm.lotID ? 'Edit Quality Control Lot' : 'Register New Quality Control Lot'}
                </h3>
                <p>Register media, antibiotic discs and staining reagents for laboratory quality traceability.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsLotModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveLot}>
              <div className="micro-modal-body">
                {/* Section 1: Lot Identity */}
                <div className="micro-section-card">
                  <div className="micro-section-title">
                    <span>1. Item Identification</span>
                    <span className="section-badge">Required</span>
                  </div>

                  <div className="micro-form-grid-2">
                    <div className="micro-form-group">
                      <label>Item Type <span className="req-star">*</span></label>
                      <select
                        value={lotForm.itemType}
                        onChange={(e) => {
                          const newType = e.target.value;
                          setLotForm({
                            ...lotForm,
                            itemType: newType,
                            itemName:
                              newType === 'Antibiotic_Disc'
                                ? 'Ceftriaxone 30 µg Disc'
                                : newType === 'Culture_Media'
                                ? 'Blood Agar Base (5% Sheep Blood)'
                                : 'Gram Stain Kit'
                          });
                        }}
                      >
                        <option value="Antibiotic_Disc">Antibiotic Disc</option>
                        <option value="Culture_Media">Culture Media Plate / Broth</option>
                        <option value="Reagent">Reagent / Staining Kit</option>
                      </select>
                    </div>

                    {lotForm.itemType === 'Antibiotic_Disc' ? (
                      <div className="micro-form-group">
                        <label>Antimicrobial Master Reference</label>
                        <select
                          value={lotForm.antimicrobialID}
                          onChange={(e) => {
                            const drug = antimicrobials.find(a => a.antimicrobialID === parseInt(e.target.value));
                            setLotForm({
                              ...lotForm,
                              antimicrobialID: e.target.value,
                              itemName: drug ? `${drug.drugName} ${lotForm.potency} Disc` : lotForm.itemName
                            });
                          }}
                        >
                          {antimicrobials.map((a) => (
                            <option key={a.antimicrobialID} value={a.antimicrobialID}>
                              {a.drugName} ({a.drugCode})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="micro-form-group">
                        <label>Storage Location</label>
                        <input
                          type="text"
                          value={lotForm.storageLocation}
                          onChange={(e) => setLotForm({ ...lotForm, storageLocation: e.target.value })}
                          placeholder="e.g. Cold Room Shelf 3"
                        />
                      </div>
                    )}
                  </div>

                  <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                    <div className="micro-form-group">
                      <label>
                        Item / Product Name <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        value={lotForm.itemName}
                        className={lotErrors.itemName ? 'is-invalid' : ''}
                        onChange={(e) => {
                          setLotForm({ ...lotForm, itemName: e.target.value });
                          setLotErrors({ ...lotErrors, itemName: null });
                        }}
                        placeholder="e.g. Ceftriaxone 30 µg Disc or CLED Agar"
                      />
                      {lotErrors.itemName && <span className="micro-form-error">{lotErrors.itemName}</span>}
                    </div>

                    <div className="micro-form-group">
                      <label>
                        Manufacturer Lot Number <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        value={lotForm.lotNumber}
                        className={lotErrors.lotNumber ? 'is-invalid' : ''}
                        onChange={(e) => {
                          setLotForm({ ...lotForm, lotNumber: e.target.value.toUpperCase() });
                          setLotErrors({ ...lotErrors, lotNumber: null });
                        }}
                        style={{ fontFamily: 'ui-monospace, monospace', fontWeight: '600' }}
                        placeholder="e.g. BD-CRO-2026A"
                      />
                      {lotErrors.lotNumber && <span className="micro-form-error">{lotErrors.lotNumber}</span>}
                    </div>
                  </div>
                </div>

                {/* Section 2: Manufacturer & Dates */}
                <div className="micro-section-card">
                  <div className="micro-section-title">
                    <span>2. Manufacturer & Expiry</span>
                    <span className="section-badge">Traceability</span>
                  </div>

                  <div className="micro-form-grid-2">
                    <div className="micro-form-group">
                      <label>
                        Manufacturer <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        value={lotForm.manufacturer}
                        className={lotErrors.manufacturer ? 'is-invalid' : ''}
                        onChange={(e) => {
                          setLotForm({ ...lotForm, manufacturer: e.target.value });
                          setLotErrors({ ...lotErrors, manufacturer: null });
                        }}
                        placeholder="e.g. Becton Dickinson (BD BBL), Oxoid, HiMedia"
                      />
                      {lotErrors.manufacturer && <span className="micro-form-error">{lotErrors.manufacturer}</span>}
                    </div>

                    <div className="micro-form-group">
                      <label>Storage Condition <span className="req-star">*</span></label>
                      <select
                        value={lotForm.storageCondition}
                        onChange={(e) => setLotForm({ ...lotForm, storageCondition: e.target.value })}
                      >
                        <option value="Refrigerated 2-8°C">Refrigerated 2-8°C</option>
                        <option value="Deep Freeze -20°C">Deep Freeze -20°C</option>
                        <option value="Room Temperature 20-25°C">Room Temperature 20-25°C</option>
                        <option value="Protected from Light">Protected from Light (Amber Container)</option>
                      </select>
                    </div>
                  </div>

                  <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                    <div className="micro-form-group">
                      <label>Received Date <span className="req-star">*</span></label>
                      <input
                        type="date"
                        value={lotForm.receivedDate}
                        onChange={(e) => setLotForm({ ...lotForm, receivedDate: e.target.value })}
                        required
                      />
                    </div>

                    <div className="micro-form-group">
                      <label>Expiry Date <span className="req-star">*</span></label>
                      <input
                        type="date"
                        value={lotForm.expiryDate}
                        className={lotErrors.expiryDate ? 'is-invalid' : ''}
                        onChange={(e) => {
                          setLotForm({ ...lotForm, expiryDate: e.target.value });
                          setLotErrors({ ...lotErrors, expiryDate: null });
                        }}
                        required
                      />
                      {lotErrors.expiryDate && <span className="micro-form-error">{lotErrors.expiryDate}</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="micro-modal-footer">
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => setIsLotModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="micro-btn-primary"
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Saving...' : 'Save QC Lot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 2: RECORD ATCC CONTROL STRAIN RUN                   */}
      {/* ========================================================= */}
      {isRunModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '760px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Record ATCC Reference Strain QC Run
                </h3>
                <p>Verify susceptibility testing performance against CLSI/EUCAST reference standard control strains.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsRunModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveRun}>
              <div className="micro-modal-body">
                {/* Strain & Antimicrobial Scope */}
                <div className="micro-section-card">
                  <div className="micro-section-title">
                    <span>1. Reference Organism & Antimicrobial</span>
                    <span className="section-badge">CLSI / EUCAST Standard</span>
                  </div>

                  <div className="micro-form-grid-2">
                    <div className="micro-form-group">
                      <label>ATCC Reference Strain <span className="req-star">*</span></label>
                      <select
                        value={runForm.controlStrain}
                        onChange={(e) => handleStrainOrDrugChange(e.target.value, runForm.antimicrobialID)}
                      >
                        <option value="Escherichia coli ATCC 25922">Escherichia coli ATCC 25922 (Gram-Negative Standard)</option>
                        <option value="Staphylococcus aureus ATCC 25923">Staphylococcus aureus ATCC 25923 (Gram-Positive Standard)</option>
                        <option value="Pseudomonas aeruginosa ATCC 27853">Pseudomonas aeruginosa ATCC 27853 (Non-Fermenter Standard)</option>
                        <option value="Enterococcus faecalis ATCC 29212">Enterococcus faecalis ATCC 29212 (Enterococcal Standard)</option>
                        <option value="Streptococcus pneumoniae ATCC 49619">Streptococcus pneumoniae ATCC 49619 (Fastidious Standard)</option>
                      </select>
                    </div>

                    <div className="micro-form-group">
                      <label>Antimicrobial Tested <span className="req-star">*</span></label>
                      <select
                        value={runForm.antimicrobialID}
                        onChange={(e) => handleStrainOrDrugChange(runForm.controlStrain, e.target.value)}
                      >
                        {antimicrobials.map((a) => (
                          <option key={a.antimicrobialID} value={a.antimicrobialID}>
                            {a.drugName} ({a.drugCode})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                    <div className="micro-form-group">
                      <label>Linked Disc Lot (Traceability)</label>
                      <select
                        value={runForm.lotID}
                        onChange={(e) => setRunForm({ ...runForm, lotID: e.target.value })}
                      >
                        <option value="">-- No specific lot linked --</option>
                        {lots
                          .filter(l => l.itemType === 'Antibiotic_Disc' && !getExpiryState(l.expiryDate).isExpired)
                          .map((l) => (
                            <option key={l.lotID} value={l.lotID}>
                              {l.itemName} (Lot: {l.lotNumber})
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="micro-form-group">
                      <label>Test Method <span className="req-star">*</span></label>
                      <select
                        value={runForm.astMethodID}
                        onChange={(e) => setRunForm({ ...runForm, astMethodID: e.target.value })}
                      >
                        {astMethods.map((m) => (
                          <option key={m.astMethodID} value={m.astMethodID}>
                            {m.methodName} ({m.measuredUnit})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Range & Observed Value */}
                <div className="micro-section-card">
                  <div className="micro-section-title">
                    <span>2. Measurement & Real-Time Validation</span>
                    <span className="section-badge">Outcome</span>
                  </div>

                  <div className="micro-form-grid-3">
                    <div className="micro-form-group">
                      <label>CLSI/EUCAST Expected Range <span className="req-star">*</span></label>
                      <input
                        type="text"
                        value={runForm.expectedRange}
                        onChange={(e) => {
                          const newRange = e.target.value;
                          const evalRes = evaluateQCResult(runForm.observedValue, newRange);
                          setRunForm({ ...runForm, expectedRange: newRange, qcStatus: evalRes.status });
                        }}
                        placeholder="e.g. 29 - 35 mm"
                        style={{ fontFamily: 'ui-monospace, monospace', fontWeight: '600' }}
                      />
                    </div>

                    <div className="micro-form-group">
                      <label>Observed Value (mm) <span className="req-star">*</span></label>
                      <input
                        type="number"
                        step="0.1"
                        value={runForm.observedValue}
                        className={runErrors.observedValue ? 'is-invalid' : ''}
                        onChange={(e) => handleObservedValueChange(e.target.value)}
                        placeholder="e.g. 31"
                        style={{ fontWeight: '700', fontSize: '1rem' }}
                      />
                      {runErrors.observedValue && <span className="micro-form-error">{runErrors.observedValue}</span>}
                    </div>

                    <div className="micro-form-group">
                      <label>Calculated QC Result</label>
                      <div style={{ marginTop: '4px' }}>
                        {runForm.qcStatus === 'Pass' ? (
                          <span className="micro-badge" style={{ background: '#dcfce7', color: '#15803d', fontSize: '0.9rem', padding: '6px 12px' }}>
                            ✓ PASS (Within Range)
                          </span>
                        ) : (
                          <span className="micro-badge" style={{ background: '#fee2e2', color: '#b91c1c', fontSize: '0.9rem', padding: '6px 12px' }}>
                            ✗ FAIL (Out of Range)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {runForm.qcStatus === 'Fail' && (
                    <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fff1f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#b91c1c', fontSize: '0.84rem' }}>
                      ⚠️ <strong>QC Failure Detected:</strong> Observed measurement is outside the acceptable standard reference range. Check disc expiry, inoculum density (0.5 McFarland) and incubator temperature.
                    </div>
                  )}

                  <div className="micro-form-grid-2" style={{ marginTop: '12px' }}>
                    <div className="micro-form-group">
                      <label>Microbiologist / Operator <span className="req-star">*</span></label>
                      <input
                        type="text"
                        value={runForm.performedByName}
                        onChange={(e) => setRunForm({ ...runForm, performedByName: e.target.value })}
                        required
                      />
                    </div>

                    <div className="micro-form-group">
                      <label>Test Date <span className="req-star">*</span></label>
                      <input
                        type="date"
                        value={runForm.runDate}
                        onChange={(e) => setRunForm({ ...runForm, runDate: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="micro-form-group" style={{ marginTop: '12px' }}>
                    <label>Remarks & QC Documentation</label>
                    <input
                      type="text"
                      value={runForm.remarks}
                      onChange={(e) => setRunForm({ ...runForm, remarks: e.target.value })}
                      placeholder="e.g. Standard 0.5 McFarland inoculum verified, incubated at 35°C for 18h"
                    />
                  </div>
                </div>
              </div>

              <div className="micro-modal-footer">
                <button
                  type="button"
                  className="micro-btn-secondary"
                  onClick={() => setIsRunModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="micro-btn-primary"
                  disabled={saveLoading}
                >
                  {saveLoading ? 'Saving...' : 'Save QC Result'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL: ADD/EDIT EXPECTED RANGE                             */}
      {/* ========================================================= */}
      {isRangeModalOpen && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '560px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                  Add / Edit QC Expected Range
                </h3>
                <p>Define the expected zone/MIC range for an ATCC strain + antimicrobial combo, used to auto-pass/fail QC runs.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setIsRangeModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="micro-modal-body">
              <div className="micro-form-group">
                <label>ATCC Reference Strain <span className="req-star">*</span></label>
                <select
                  value={rangeForm.atccStrainName}
                  onChange={(e) => setRangeForm({ ...rangeForm, atccStrainName: e.target.value })}
                >
                  {ATCC_CONTROL_STRAINS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {rangeErrors.atccStrainName && <span className="micro-form-error">{rangeErrors.atccStrainName}</span>}
              </div>

              <div className="micro-form-group">
                <label>Antimicrobial <span className="req-star">*</span></label>
                <select
                  value={rangeForm.antimicrobialID}
                  onChange={(e) => setRangeForm({ ...rangeForm, antimicrobialID: e.target.value })}
                >
                  {antimicrobials.map((a) => (
                    <option key={a.antimicrobialID} value={a.antimicrobialID}>
                      {a.drugName} ({a.drugCode})
                    </option>
                  ))}
                </select>
                {rangeErrors.antimicrobialID && <span className="micro-form-error">{rangeErrors.antimicrobialID}</span>}
              </div>

              <div className="micro-form-grid-2">
                <div className="micro-form-group">
                  <label>Method</label>
                  <select
                    value={rangeForm.astMethod}
                    onChange={(e) => setRangeForm({ ...rangeForm, astMethod: e.target.value })}
                  >
                    <option value="Zone">Zone (mm)</option>
                    <option value="MIC">MIC (µg/mL)</option>
                  </select>
                </div>
                <div className="micro-form-group">
                  <label>Expected Range <span className="req-star">*</span></label>
                  <input
                    type="text"
                    value={rangeForm.expectedRangeText}
                    onChange={(e) => setRangeForm({ ...rangeForm, expectedRangeText: e.target.value })}
                    placeholder="e.g. 29 - 35 mm"
                  />
                  {rangeErrors.expectedRangeText && <span className="micro-form-error">{rangeErrors.expectedRangeText}</span>}
                </div>
              </div>
            </div>

            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-secondary"
                onClick={() => setIsRangeModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="micro-btn-primary"
                disabled={saveLoading}
                onClick={handleSaveRange}
              >
                {saveLoading ? 'Saving...' : 'Save Expected Range'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 3: VIEW RECORD DETAILS                              */}
      {/* ========================================================= */}
      {viewDetailModal.isOpen && viewDetailModal.data && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '640px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>Quality Control Record Details</h3>
                <p>Complete traceability and verification documentation.</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setViewDetailModal({ isOpen: false, type: null, data: null })}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body">
              {viewDetailModal.type === 'lot' && (
                <div className="micro-section-card">
                  <div className="micro-section-title">Media / Disc Lot Specifications</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.88rem' }}>
                    <div><strong>Item Name:</strong> {viewDetailModal.data.itemName}</div>
                    <div><strong>Item Type:</strong> {viewDetailModal.data.itemType?.replace('_', ' ')}</div>
                    <div><strong>Lot Number:</strong> <code>{viewDetailModal.data.lotNumber}</code></div>
                    <div><strong>Manufacturer:</strong> {viewDetailModal.data.manufacturer}</div>
                    <div><strong>Received Date:</strong> {formatSafeDate(viewDetailModal.data.receivedDate)}</div>
                    <div><strong>Expiry Date:</strong> {formatSafeDate(viewDetailModal.data.expiryDate)}</div>
                    <div><strong>Storage Condition:</strong> {viewDetailModal.data.storageCondition || 'Refrigerated 2-8°C'}</div>
                    <div><strong>Status:</strong> {viewDetailModal.data.isActive ? 'Active In-Use' : 'Inactive / Quarantined'}</div>
                  </div>
                </div>
              )}

              {viewDetailModal.type === 'run' && (
                <div className="micro-section-card">
                  <div className="micro-section-title">ATCC Reference Strain QC Run Verification</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.88rem' }}>
                    <div><strong>Control Strain:</strong> {viewDetailModal.data.controlStrain || viewDetailModal.data.qcOrganism}</div>
                    <div><strong>Antimicrobial:</strong> {viewDetailModal.data.drugName ? `${viewDetailModal.data.drugName} (${viewDetailModal.data.drugCode})` : (viewDetailModal.data.antimicrobialName || 'Ceftriaxone')}</div>
                    <div><strong>Test Date:</strong> {formatSafeDate(viewDetailModal.data.runDate || viewDetailModal.data.testDate, true)}</div>
                    <div><strong>Expected Range:</strong> <code>{viewDetailModal.data.expectedRange}</code></div>
                    <div><strong>Observed Value:</strong> <strong>{viewDetailModal.data.observedValue ?? viewDetailModal.data.measuredValue} mm</strong></div>
                    <div><strong>Result:</strong> <span className="micro-badge" style={{ background: (viewDetailModal.data.qcStatus || viewDetailModal.data.status) === 'Pass' ? '#dcfce7' : '#fee2e2', color: (viewDetailModal.data.qcStatus || viewDetailModal.data.status) === 'Pass' ? '#15803d' : '#b91c1c' }}>{(viewDetailModal.data.qcStatus || viewDetailModal.data.status || 'Pass').toUpperCase()}</span></div>
                    <div><strong>Microbiologist / Operator:</strong> {viewDetailModal.data.performedByName || viewDetailModal.data.operatorName || 'Senior Microbiologist'}</div>
                    <div><strong>Remarks:</strong> {viewDetailModal.data.remarks || 'Standard verified inoculum'}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-primary"
                onClick={() => setViewDetailModal({ isOpen: false, type: null, data: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL 4: AUDIT & HISTORY                                  */}
      {/* ========================================================= */}
      {historyModal.isOpen && historyModal.data && (
        <div className="micro-modal-overlay">
          <div className="micro-modal-content" style={{ maxWidth: '600px' }}>
            <div className="micro-modal-header">
              <div className="micro-modal-header-titles">
                <h3>📜 Audit & Traceability History</h3>
                <p>{historyModal.data.lotNumber || historyModal.data.controlStrain || 'QC Traceability Record'}</p>
              </div>
              <button
                type="button"
                className="micro-modal-close-btn"
                onClick={() => setHistoryModal({ isOpen: false, type: null, data: null })}
              >
                &times;
              </button>
            </div>
            <div className="micro-modal-body">
              <div className="micro-section-card">
                <div className="micro-section-title">Compliance Audit Trail</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem' }}>
                  <div><strong>ISO 15189 / NABL Requirement:</strong> Quality control performance verification</div>
                  <div><strong>Logged Record Timestamp:</strong> {formatSafeDate(historyModal.data.runDate || historyModal.data.receivedDate || new Date(), true)}</div>
                  <div><strong>Verified By:</strong> Authorized Microbiologist / Quality Lead</div>
                  <div><strong>Inspection Status:</strong> Routine Quality Audit Compliant</div>
                </div>
              </div>
            </div>
            <div className="micro-modal-footer">
              <button
                type="button"
                className="micro-btn-primary"
                onClick={() => setHistoryModal({ isOpen: false, type: null, data: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
